import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProgressLoader } from "@/components/molecules/ProgressLoader";
import { PortalLayout } from "@/layouts/PortalLayout";
import { PublicLayout } from "@/layouts/PublicLayout";
import { useAuth } from "@/features/auth/AuthProvider";
import { useGuestBookingEnabled } from "@/features/appointments/hooks/useGuestBooking";
import type { Portal } from "@/features/auth/types";
import { GuestOnly, HomeRedirect, RequireAuth, RequirePermission, RequirePortal, SessionLoader } from "./guards";
import { PORTAL_HOME, ROUTES } from "./routes";

// Route-level code splitting keeps the first load small.
const named = <T extends Record<string, unknown>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() => loader().then((module) => ({ default: module[name] as React.ComponentType })));

const LoginPage = named(() => import("@/features/auth/pages/LoginPage"), "LoginPage");
const RegisterPage = named(() => import("@/features/auth/pages/RegisterPage"), "RegisterPage");
const ForgotPasswordPage = named(() => import("@/features/auth/pages/ForgotPasswordPage"), "ForgotPasswordPage");
const ProfilePage = named(() => import("@/features/account/pages/ProfilePage"), "ProfilePage");
const SecurityPage = named(() => import("@/features/account/pages/SecurityPage"), "SecurityPage");
const PortalHome = lazy(() => import("@/features/portals/PortalHome").then((m) => ({ default: m.PortalHome })));
// These two take a `guest` prop (public booking), so they keep their prop types.
const FindDoctorsPage = lazy(() => import("@/features/doctors/pages/FindDoctorsPage").then((m) => ({ default: m.FindDoctorsPage })));
const DoctorBookingPage = lazy(() => import("@/features/doctors/pages/DoctorBookingPage").then((m) => ({ default: m.DoctorBookingPage })));
const PatientAppointmentsPage = named(() => import("@/features/appointments/pages/PatientAppointmentsPage"), "PatientAppointmentsPage");
const DoctorAppointmentsPage = named(() => import("@/features/appointments/pages/DoctorAppointmentsPage"), "DoctorAppointmentsPage");
const DoctorSchedulePage = named(() => import("@/features/schedule/pages/DoctorSchedulePage"), "DoctorSchedulePage");
const DoctorSelfProfilePage = named(() => import("@/features/doctors/pages/DoctorSelfProfilePage"), "DoctorSelfProfilePage");
const DoctorsAdminPage = named(() => import("@/features/doctors/pages/DoctorsAdminPage"), "DoctorsAdminPage");
const DoctorEditorPage = named(() => import("@/features/doctors/pages/DoctorEditorPage"), "DoctorEditorPage");
const AppointmentsAdminPage = named(() => import("@/features/appointments/pages/AppointmentsAdminPage"), "AppointmentsAdminPage");
const MasterDataPage = named(() => import("@/features/master-data/pages/MasterDataPage"), "MasterDataPage");

function PageLoader() {
  const { t } = useTranslation();
  return <ProgressLoader className="min-h-[50vh]" messages={[t("loader.loadingPage"), t("loader.almostDone")]} />;
}

const page = (element: ReactNode) => <Suspense fallback={<PageLoader />}>{element}</Suspense>;
const PORTALS: Portal[] = ["PATIENT", "DOCTOR", "ADMIN", "EXECUTIVE"];

/** Wraps a portal page: correct portal context, optional permission, lazy loading. */
const portalPage = (portal: Portal, element: ReactNode, permission?: string) => (
  <RequirePortal portal={portal}>{page(permission ? <RequirePermission permission={permission}>{element}</RequirePermission> : element)}</RequirePortal>
);

/**
 * Shell for the doctor search/booking pages, which also work without signing in
 * when the API allows guest booking (shared doctor links): signed-in users get
 * the portal, guests the public shell; with guest booking off, guests sign in first.
 */
function PortalOrGuestLayout() {
  const { status } = useAuth();
  const location = useLocation();
  const guestBooking = useGuestBookingEnabled();
  if (status === "loading" || (status === "anonymous" && guestBooking.isPending)) return <SessionLoader />;
  if (status === "authenticated") return <PortalLayout />;
  if (guestBooking.enabled) return <PublicLayout />;
  return <Navigate to={ROUTES.LOGIN} replace state={{ from: location.pathname + location.search }} />;
}

function DoctorBookingRoute() {
  const { status } = useAuth();
  return status === "authenticated" ? portalPage("PATIENT", <DoctorBookingPage />, "appointments:book:own") : page(<DoctorBookingPage guest />);
}

function PublicFindDoctorsRoute() {
  const { status } = useAuth();
  return status === "authenticated" ? <Navigate to={ROUTES.PATIENT_FIND_DOCTORS} replace /> : page(<FindDoctorsPage guest />);
}

export function AppRouter() {
  return (
    <Routes>
      <Route path={ROUTES.ROOT} element={<HomeRedirect />} />
      <Route path={ROUTES.LOGIN} element={<GuestOnly>{page(<LoginPage />)}</GuestOnly>} />
      <Route path={ROUTES.REGISTER} element={<GuestOnly>{page(<RegisterPage />)}</GuestOnly>} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<GuestOnly>{page(<ForgotPasswordPage />)}</GuestOnly>} />

      <Route element={<PortalOrGuestLayout />}>
        <Route path={ROUTES.BOOK} element={<PublicFindDoctorsRoute />} />
        <Route path={ROUTES.PATIENT_DOCTOR} element={<DoctorBookingRoute />} />
      </Route>

      <Route element={<RequireAuth><PortalLayout /></RequireAuth>}>
        {PORTALS.map((portal) => (
          <Route key={portal} path={PORTAL_HOME[portal]} element={portalPage(portal, <PortalHome portal={portal} />)} />
        ))}

        <Route path={ROUTES.PATIENT_FIND_DOCTORS} element={portalPage("PATIENT", <FindDoctorsPage />)} />
        <Route path={ROUTES.PATIENT_APPOINTMENTS} element={portalPage("PATIENT", <PatientAppointmentsPage />, "appointments:read:own")} />

        <Route path={ROUTES.DOCTOR_APPOINTMENTS} element={portalPage("DOCTOR", <DoctorAppointmentsPage />, "appointments:read:doctor")} />
        <Route path={ROUTES.DOCTOR_SCHEDULE} element={portalPage("DOCTOR", <DoctorSchedulePage />, "schedules:manage:own")} />
        <Route path={ROUTES.DOCTOR_PROFILE} element={portalPage("DOCTOR", <DoctorSelfProfilePage />, "doctor-profile:update:own")} />

        <Route path={ROUTES.ADMIN_APPOINTMENTS} element={portalPage("ADMIN", <AppointmentsAdminPage />, "appointments:read")} />
        <Route path={ROUTES.ADMIN_DOCTORS} element={portalPage("ADMIN", <DoctorsAdminPage />, "doctors:read")} />
        <Route path={ROUTES.ADMIN_DOCTOR_NEW} element={portalPage("ADMIN", <DoctorEditorPage />, "doctors:manage")} />
        <Route path={ROUTES.ADMIN_DOCTOR_DETAIL} element={portalPage("ADMIN", <DoctorEditorPage />, "doctors:read")} />
        <Route path={ROUTES.ADMIN_MASTER_DATA} element={portalPage("ADMIN", <MasterDataPage />, "master-data:read")} />

        <Route path={ROUTES.EXECUTIVE_APPOINTMENTS} element={portalPage("EXECUTIVE", <AppointmentsAdminPage />, "appointments:read")} />

        <Route path={ROUTES.ACCOUNT_PROFILE} element={page(<ProfilePage />)} />
        <Route path={ROUTES.ACCOUNT_SECURITY} element={page(<SecurityPage />)} />
      </Route>

      <Route path={ROUTES.NOT_FOUND} element={<Navigate to={ROUTES.ROOT} replace />} />
    </Routes>
  );
}
